import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * The optimistic half of a drag.
 *
 * A dragged card has to land in its new column immediately, so the cached list
 * is patched before the write goes out and restored if the write is refused.
 * useMoveTaskStatus has done this since Phase 5; three more boards need the
 * same thing, and writing it four times is three chances to get the rollback
 * subtly wrong.
 *
 * The rollback restores the WHOLE previous list rather than re-patching the
 * one row back. A refused write can coincide with anything else the list
 * changed, and putting one field back would leave the rest of the cache
 * describing a state that never existed.
 */

export interface OptimisticContext<T> {
  previous: T | undefined;
}

/**
 * Patch one row of a cached array in place, keeping order.
 *
 * Returns the previous cache value so onError can put it back untouched. In
 * flight queries are cancelled first: a refetch that started before the drag
 * would otherwise land after the patch and overwrite it with the old value,
 * which looks exactly like the write failing.
 */
export async function patchCachedRow<Row extends { id: string }>(
  queryClient: QueryClient,
  key: QueryKey,
  rowId: string,
  changes: Partial<Row>,
): Promise<OptimisticContext<Row[]>> {
  await queryClient.cancelQueries({ queryKey: key });
  const previous = queryClient.getQueryData<Row[]>(key);
  if (previous) {
    queryClient.setQueryData<Row[]>(
      key,
      previous.map((row) => (row.id === rowId ? { ...row, ...changes } : row)),
    );
  }
  return { previous };
}

/** Drop one row from a cached array, for an optimistic delete. */
export async function removeCachedRow<Row extends { id: string }>(
  queryClient: QueryClient,
  key: QueryKey,
  rowId: string,
): Promise<OptimisticContext<Row[]>> {
  await queryClient.cancelQueries({ queryKey: key });
  const previous = queryClient.getQueryData<Row[]>(key);
  if (previous) {
    queryClient.setQueryData<Row[]>(
      key,
      previous.filter((row) => row.id !== rowId),
    );
  }
  return { previous };
}

/**
 * Put the cache back exactly as it was.
 *
 * Does nothing when there was no cached value to begin with: writing
 * `undefined` would evict the query and make the list flash empty on a failure
 * whose whole point is that nothing should appear to have happened.
 */
export function restoreCachedRows<T>(
  queryClient: QueryClient,
  key: QueryKey,
  context: OptimisticContext<T> | undefined,
): void {
  if (context?.previous !== undefined) {
    queryClient.setQueryData(key, context.previous);
  }
}

/**
 * Patch several rows of a cached array at once, for a reorder.
 *
 * A reorder can touch one row (a midpoint) or every row in a section (a
 * reindex), and both go through here so the list settles the moment the row is
 * dropped rather than after the round trip.
 *
 * Rows not named in `changes` are left exactly as they are, so this is safe on
 * a cache holding more than the section being reordered.
 */
export async function patchCachedRows<Row extends { id: string }>(
  queryClient: QueryClient,
  key: QueryKey,
  changes: { id: string; changes: Partial<Row> }[],
): Promise<OptimisticContext<Row[]>> {
  await queryClient.cancelQueries({ queryKey: key });
  const previous = queryClient.getQueryData<Row[]>(key);
  if (previous) {
    const byId = new Map(changes.map((entry) => [entry.id, entry.changes]));
    queryClient.setQueryData<Row[]>(
      key,
      previous.map((row) => {
        const patch = byId.get(row.id);
        return patch ? { ...row, ...patch } : row;
      }),
    );
  }
  return { previous };
}
